import type { RealtimeEvent } from "./realtime.types";

export type LegacyRealtimeMessage =
  | {
      t: "hello";
      playerId: string;
    }
  | {
      t: "error";
      message: string;
    }
  | {
      t: "left";
    }
  | {
      t: "joined";
      lobbyId: string;
      playerId: string;
    }
  | {
      t: "lobby_state";
      lobbyId: string;
      players: Array<{
        id: string;
        name: string;
        color?: string;
        ready?: boolean;
      }>;
      started: boolean;
      secondsUntilStart: number;
      teamMode?: string;
    }
  | {
      t: "lobby_list";
      lobbies: Array<{
        id: string;
        playerCount: number;
        maxPlayers: number;
        spectatorCount?: number;
        status:
          | "forming"
          | "waiting"
          | "countdown"
          | "starting"
          | "in_progress"
          | "finished";
        secondsUntilStart: number;
        createdAt: number;
        startTime?: number;
        estimatedDuration?: number;
        hostUserId?: string;
        hostName?: string;
        schemaVersion?: number;
        gameMode?: string;
        teamMode?: string;
        gameConfig: {
          mode: string;
          difficulty: "easy" | "normal" | "hard" | "expert";
        };
      }>;
    }
  | {
      t: "party_state";
      party: {
        id: string;
        joinCode: string;
        leader: string;
        members: Array<{
          id: string;
          name: string;
          steamId?: string;
          ready: boolean;
        }>;
      } | null;
    }
  | {
      t: "player_joined";
      playerName: string;
    }
  | {
      t: "player_left";
      playerName: string;
    }
  | {
      t: "player_killed";
      killerId: string;
      victimId: string;
      killerName: string;
      victimName: string;
      deathX: number;
      deathY: number;
      deathVx: number;
      deathVy: number;
    }
  | {
      t: "player_crashed";
      playerId: string;
      playerName: string;
      speed?: number;
    }
  | {
      t: "player_crashed_asteroid";
      playerId: string;
      playerName: string;
    }
  | {
      t: "player_collision_death";
      playerId: string;
      playerName: string;
      killerId?: string;
      killerName?: string;
    }
  | {
      t: "player_mine_death";
      playerId: string;
      playerName: string;
      killerId?: string;
      killerName?: string;
    }
  | {
      t: "eliminated";
      eliminatedBy?: string;
      eliminatedByName?: string;
      reason?: string;
    }
  | {
      t: "game_over";
      winnerId: string;
      winnerName: string;
      winningTeamId?: number;
      isBot?: boolean;
    }
  | {
      t: "player_eliminated";
      playerId: string;
      playerName: string;
      eliminatedBy?: string;
      eliminatedByName?: string;
      remainingPlayers?: number;
    }
  | {
      t: "game_end";
      winner: number;
      isDraw: boolean;
      reason: string;
      finalScores: Record<number, number>;
      playerStats: Array<{
        playerId: string;
        playerName: string;
        kills: number;
        deaths: number;
        pelletsCollected: number;
        score: number;
        teamId?: number;
      }>;
    }
  | {
      t: "autoplay_enabled";
      playerId: string;
    }
  | {
      t: "autoplay_disabled";
      playerId: string;
    }
  | {
      t: "bot_command_applied";
      playerId: string;
      behavior: string;
    }
  | {
      t: "damage_batch";
      events: Array<{
        x: number;
        y: number;
        damage: number;
        source: string;
      }>;
    }
  | {
      t: "damage_dealt";
      x: number;
      y: number;
      damage: number;
      source: string;
    }
  | {
      t: "player_count";
      count: number;
    }
  | {
      t: "asteroid_destroyed";
      x: number;
      y: number;
      sizeLevel: number;
      vx?: number;
      vy?: number;
    }
  | {
      t: "mine_exploded";
      x: number;
      y: number;
      radius: number;
    }
  | {
      t: "mine_deployed";
      mine: {
        id: string;
        ownerId?: string;
        pos: { x: number; y: number };
        vel: { x: number; y: number };
        health: number;
        maxHealth: number;
        deployTime: number;
        radius: number;
      };
    }
  | {
      t: "flags_update";
      flags: Array<{
        id: string;
        teamId: number;
        pos?: { x: number; y: number };
        state: "home" | "carried" | "dropped";
        carrierId?: string;
        droppedAt?: number;
      }>;
      teamScores: Record<number, number>;
      captureLimit: number;
    }
  | {
      t: "flag_taken";
      teamId: number;
      playerId: string;
      playerName: string;
    }
  | {
      t: "flag_dropped";
      teamId: number;
      playerId: string;
      playerName: string;
      x: number;
      y: number;
    }
  | {
      t: "flag_returned";
      teamId: number;
      playerId: string;
      playerName: string;
    }
  | {
      t: "flag_captured";
      flagTeamId: number;
      capturingTeamId: number;
      playerId: string;
      playerName: string;
      newScore: number;
    }
  | {
      t: "moba_state";
      bases: Array<{
        id: string;
        teamId: number;
        pos: { x: number; y: number };
        health: number;
        maxHealth: number;
        radius: number;
      }>;
      npcs: Array<{
        id: string;
        teamId: number;
        laneId: string;
        pos: { x: number; y: number };
        vel: { x: number; y: number };
        health: number;
        maxHealth: number;
        radius: number;
      }>;
      teamScores: Record<number, number>;
      waveNumber: number;
    }
  | {
      t: "base_damaged";
      baseTeamId: number;
      damage: number;
      newHealth: number;
      attackerId: string | null;
      attackerName: string | null;
    }
  | {
      t: "base_destroyed";
      baseTeamId: number;
      destroyedBy: string | null;
      destroyedByName: string | null;
    }
  | {
      t: "npc_killed";
      npcId: string;
      killerId: string | null;
      killerName: string | null;
      xpAwarded: number;
    }
  | {
      t: "wave_spawned";
      waveNumber: number;
      npcsSpawned: number;
    }
  | {
      t: "pong";
      now?: number;
    }
  | {
      t: "game_delta";
      timestamp: number;
      sequence: number;
      createdPlayers: unknown[];
      updatedPlayers: unknown[];
      deletedPlayerIds: string[];
      createdBullets: unknown[];
      updatedBullets: unknown[];
      deletedBulletIds: string[];
      createdAsteroids: unknown[];
      updatedAsteroids: unknown[];
      deletedAsteroidIds: string[];
      createdPellets: unknown[];
      updatedPellets: unknown[];
      deletedPelletIds: string[];
      createdMines: unknown[];
      updatedMines: unknown[];
      deletedMineIds: string[];
      gameModeState?: {
        mode: string;
        teamScores: Record<number, number>;
        captureLimit?: number;
        flags: Array<{
          id: string;
          teamId: number;
          pos?: { x: number; y: number };
          state: "home" | "carried" | "dropped";
          carrierId?: string;
          droppedAt?: number;
        }>;
      };
      performance?: {
        tps: number;
        frameTime: number;
        entities: {
          players: number;
          bullets: number;
          asteroids: number;
          pellets: number;
        };
      };
    }
  | {
      t: "game_state";
      players: Array<{
        id: string;
        name: string;
        isBot?: boolean;
        color: string;
        pos: { x: number; y: number };
        vel: { x: number; y: number };
        rotation: number;
        targetRotation: number;
        angularVelocity: number;
        health: number;
        maxHealth: number;
        kills: number;
        deaths: number;
        pelletsCollected: number;
        scale: number;
        damageMultiplier: number;
        speedMultiplier: number;
        lastAcknowledgedSequence: number;
        totalKills?: number;
        totalDeaths?: number;
        totalPelletsCollected?: number;
        captures?: number;
        lastMineDeployAt?: number;
        lastBoostAt?: number;
        teamId?: number;
        primaryWeapon?: string;
      }>;
      bullets: unknown[];
      asteroids: unknown[];
      pellets: unknown[];
      mines: Array<{
        id: string;
        ownerId?: string;
        pos: { x: number; y: number };
        vel: { x: number; y: number };
        health: number;
        maxHealth: number;
        deployTime: number;
        radius: number;
      }>;
      timestamp: number;
      sequence: number;
      gameModeState?: {
        mode: string;
        teamScores: Record<number, number>;
        captureLimit?: number;
        flags: Array<{
          id: string;
          teamId: number;
          pos?: { x: number; y: number };
          state: "home" | "carried" | "dropped";
          carrierId?: string;
          droppedAt?: number;
        }>;
      };
      performance?: {
        tps: number;
        frameTime: number;
        entities: {
          players: number;
          bullets: number;
          asteroids: number;
          pellets: number;
        };
      };
    };

export interface RealtimeMessageMappingContext {
  currentLobbyId: string | null;
  currentPlayerId: string | null;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Centralized parity mapper keeps all realtime message translations in one switch.
export function mapRealtimeEventToLegacyMessages(
  event: RealtimeEvent,
  context: RealtimeMessageMappingContext,
): LegacyRealtimeMessage[] {
  switch (event.type) {
    case "connected":
      return [{ t: "hello", playerId: event.identity }];
    case "error":
      return [{ t: "error", message: event.message }];
    case "left_lobby":
      return [{ t: "left" }];
    case "connection_state":
      return [];
    case "lobby_state": {
      const messages: LegacyRealtimeMessage[] = [];
      if (context.currentLobbyId !== event.lobbyId) {
        messages.push({
          t: "joined",
          lobbyId: event.lobbyId,
          playerId: context.currentPlayerId ?? "",
        });
      }
      messages.push({
        t: "lobby_state",
        lobbyId: event.lobbyId,
        players: event.players,
        started: event.started,
        secondsUntilStart: event.secondsUntilStart,
        ...(typeof event.teamMode === "string"
          ? { teamMode: event.teamMode }
          : {}),
      });
      return messages;
    }
    case "lobby_list":
      return [{ t: "lobby_list", lobbies: event.lobbies }];
    case "party_state":
      return [{ t: "party_state", party: event.party }];
    case "player_joined":
      return [{ t: "player_joined", playerName: event.playerName }];
    case "player_left":
      return [{ t: "player_left", playerName: event.playerName }];
    case "player_killed":
      return [
        {
          t: "player_killed",
          killerId: event.killerId,
          victimId: event.victimId,
          killerName: event.killerName,
          victimName: event.victimName,
          deathX: event.deathX,
          deathY: event.deathY,
          deathVx: event.deathVx,
          deathVy: event.deathVy,
        },
      ];
    case "player_crashed":
      return [
        {
          t: "player_crashed",
          playerId: event.playerId,
          playerName: event.playerName,
          ...(typeof event.speed === "number" ? { speed: event.speed } : {}),
        },
      ];
    case "player_crashed_asteroid":
      return [
        {
          t: "player_crashed_asteroid",
          playerId: event.playerId,
          playerName: event.playerName,
        },
      ];
    case "player_collision_death":
      return [
        {
          t: "player_collision_death",
          playerId: event.playerId,
          playerName: event.playerName,
          ...(typeof event.killerId === "string"
            ? { killerId: event.killerId }
            : {}),
          ...(typeof event.killerName === "string"
            ? { killerName: event.killerName }
            : {}),
        },
      ];
    case "player_mine_death":
      return [
        {
          t: "player_mine_death",
          playerId: event.playerId,
          playerName: event.playerName,
          ...(typeof event.killerId === "string"
            ? { killerId: event.killerId }
            : {}),
          ...(typeof event.killerName === "string"
            ? { killerName: event.killerName }
            : {}),
        },
      ];
    case "eliminated":
      return [
        {
          t: "eliminated",
          ...(typeof event.eliminatedBy === "string"
            ? { eliminatedBy: event.eliminatedBy }
            : {}),
          ...(typeof event.eliminatedByName === "string"
            ? { eliminatedByName: event.eliminatedByName }
            : {}),
          ...(typeof event.reason === "string" ? { reason: event.reason } : {}),
        },
      ];
    case "game_over":
      return [
        {
          t: "game_over",
          winnerId: event.winnerId,
          winnerName: event.winnerName,
          ...(typeof event.winningTeamId === "number"
            ? { winningTeamId: event.winningTeamId }
            : {}),
          ...(event.isBot === true ? { isBot: true } : {}),
        },
      ];
    case "player_eliminated":
      return [
        {
          t: "player_eliminated",
          playerId: event.playerId,
          playerName: event.playerName,
          ...(typeof event.eliminatedBy === "string"
            ? { eliminatedBy: event.eliminatedBy }
            : {}),
          ...(typeof event.eliminatedByName === "string"
            ? { eliminatedByName: event.eliminatedByName }
            : {}),
          ...(typeof event.remainingPlayers === "number"
            ? { remainingPlayers: event.remainingPlayers }
            : {}),
        },
      ];
    case "game_end":
      return [
        {
          t: "game_end",
          winner: event.winner,
          isDraw: event.isDraw,
          reason: event.reason,
          finalScores: event.finalScores,
          playerStats: event.playerStats,
        },
      ];
    case "autoplay_enabled":
      return [
        {
          t: "autoplay_enabled",
          playerId: event.playerId,
        },
      ];
    case "autoplay_disabled":
      return [
        {
          t: "autoplay_disabled",
          playerId: event.playerId,
        },
      ];
    case "bot_command_applied":
      return [
        {
          t: "bot_command_applied",
          playerId: event.playerId,
          behavior: event.behavior,
        },
      ];
    case "damage_batch":
      return [
        {
          t: "damage_batch",
          events: event.events,
        },
      ];
    case "damage_dealt":
      return [
        {
          t: "damage_dealt",
          x: event.x,
          y: event.y,
          damage: event.damage,
          source: event.source,
        },
      ];
    case "player_count":
      return [{ t: "player_count", count: event.count }];
    case "asteroid_destroyed":
      return [
        {
          t: "asteroid_destroyed",
          x: event.x,
          y: event.y,
          sizeLevel: event.sizeLevel,
          vx: event.vx,
          vy: event.vy,
        },
      ];
    case "mine_exploded":
      return [
        {
          t: "mine_exploded",
          x: event.x,
          y: event.y,
          radius: event.radius,
        },
      ];
    case "mine_deployed":
      return [
        {
          t: "mine_deployed",
          mine: event.mine,
        },
      ];
    case "flags_update":
      return [
        {
          t: "flags_update",
          flags: event.flags,
          teamScores: event.teamScores,
          captureLimit: event.captureLimit,
        },
      ];
    case "flag_taken":
      return [
        {
          t: "flag_taken",
          teamId: event.teamId,
          playerId: event.playerId,
          playerName: event.playerName,
        },
      ];
    case "flag_dropped":
      return [
        {
          t: "flag_dropped",
          teamId: event.teamId,
          playerId: event.playerId,
          playerName: event.playerName,
          x: event.x,
          y: event.y,
        },
      ];
    case "flag_returned":
      return [
        {
          t: "flag_returned",
          teamId: event.teamId,
          playerId: event.playerId,
          playerName: event.playerName,
        },
      ];
    case "flag_captured":
      return [
        {
          t: "flag_captured",
          flagTeamId: event.flagTeamId,
          capturingTeamId: event.capturingTeamId,
          playerId: event.playerId,
          playerName: event.playerName,
          newScore: event.newScore,
        },
      ];
    case "moba_state":
      return [
        {
          t: "moba_state",
          bases: event.bases,
          npcs: event.npcs,
          teamScores: event.teamScores,
          waveNumber: event.waveNumber,
        },
      ];
    case "base_damaged":
      return [
        {
          t: "base_damaged",
          baseTeamId: event.baseTeamId,
          damage: event.damage,
          newHealth: event.newHealth,
          attackerId: event.attackerId,
          attackerName: event.attackerName,
        },
      ];
    case "base_destroyed":
      return [
        {
          t: "base_destroyed",
          baseTeamId: event.baseTeamId,
          destroyedBy: event.destroyedBy,
          destroyedByName: event.destroyedByName,
        },
      ];
    case "npc_killed":
      return [
        {
          t: "npc_killed",
          npcId: event.npcId,
          killerId: event.killerId,
          killerName: event.killerName,
          xpAwarded: event.xpAwarded,
        },
      ];
    case "wave_spawned":
      return [
        {
          t: "wave_spawned",
          waveNumber: event.waveNumber,
          npcsSpawned: event.npcsSpawned,
        },
      ];
    case "pong":
      return [
        {
          t: "pong",
          ...(typeof event.now === "number" ? { now: event.now } : {}),
        },
      ];
    case "game_delta":
      return [
        {
          t: "game_delta",
          timestamp: event.delta.timestamp,
          sequence: event.delta.sequence,
          createdPlayers: event.delta.createdPlayers,
          updatedPlayers: event.delta.updatedPlayers,
          deletedPlayerIds: event.delta.deletedPlayerIds,
          createdBullets: event.delta.createdBullets,
          updatedBullets: event.delta.updatedBullets,
          deletedBulletIds: event.delta.deletedBulletIds,
          createdAsteroids: event.delta.createdAsteroids,
          updatedAsteroids: event.delta.updatedAsteroids,
          deletedAsteroidIds: event.delta.deletedAsteroidIds,
          createdPellets: event.delta.createdPellets,
          updatedPellets: event.delta.updatedPellets,
          deletedPelletIds: event.delta.deletedPelletIds,
          createdMines: event.delta.createdMines,
          updatedMines: event.delta.updatedMines,
          deletedMineIds: event.delta.deletedMineIds,
          gameModeState: event.delta.gameModeState,
          performance: event.delta.performance,
        },
      ];
    case "game_state":
      return [
        {
          t: "game_state",
          players: event.players,
          bullets: event.bullets,
          asteroids: event.asteroids,
          pellets: event.pellets,
          mines: event.mines,
          timestamp: event.timestamp,
          sequence: event.sequence,
          gameModeState: event.gameModeState,
          performance: event.performance,
        },
      ];
  }
}
